import { useState } from "react";

import { Client } from "@/Common/Api/Client";
import { Modal } from "@/Common/Components/Modals/Modal";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { PossiblePatientWarning } from "@/Common/Models/Patient/PatientEnums";
import { PatientPossibleWarningDto } from "@/Common/Models/Patient/PatientPossibleWarningDto";
import * as ServerOperations from "@/Common/Operations/operations.server";

import "./PatientMissingInfoWarning.css";

interface IPatientMissingInfoWarningProps {
    patientContactId: string;
    warnings?: PossiblePatientWarning[];
    callback: (dto: PatientPossibleWarningDto) => void;
}

function PatientMissingInfoWarning(props: IPatientMissingInfoWarningProps): JSX.Element {
    const { getText } = useTranslation();

    const [isSexAtBirthMale, setIsSexAtBirthMale] = useState(false);
    const [isSexAtBirthFemale, setIsSexAtBirthFemale] = useState(false);
    const [isOther, setIsOther] = useState(false);
    const [selectedSexLookupId, setSelectedSexLookupId] = useState("");

    function renderSexAtBirthMandatorySection(): JSX.Element {
        const maleLookupId = cacheStore
            .GetGenderLookups()
            .firstOrDefault(p => p.LookupCode === "MALE")
            .LookupID;

        const femaleLookupId = cacheStore
            .GetGenderLookups()
            .firstOrDefault(p => p.LookupCode === "FEMALE")
            .LookupID;

        const otherLookupId = cacheStore
            .GetGenderLookups()
            .firstOrDefault(p => p.LookupCode === "OTHERGENDER")
            .LookupID;

        return (
            <>
                <div>
                    {getText("PatientSexAtBirthMandatoryWarningLabel")}
                    <br />
                    {getText("PleaseFillItInLabel")}
                </div>
                <div className="sex-missing-at-birth-body">
                    <span>{getText("SexAtBirthLabel")}</span>
                    <div className="radio-button-group">
                        <>
                            <label
                                onClick={() => {
                                    setIsSexAtBirthMale(true);
                                    setIsOther(false);
                                    setIsSexAtBirthFemale(false);
                                    setSelectedSexLookupId(maleLookupId);
                                }}
                                className="no-bottom-margin"
                            >
                                <input checked={isSexAtBirthMale} type="radio" />
                                <label className="value-label">M</label>
                            </label>
                        </>
                        <>
                            <label
                                onClick={() => {
                                    setIsSexAtBirthFemale(true);
                                    setIsSexAtBirthMale(false);
                                    setIsOther(false);
                                    setSelectedSexLookupId(femaleLookupId);
                                }}
                                className="female-label no-bottom-margin"
                            >
                                <input checked={isSexAtBirthFemale} type="radio" />
                                <label className="value-label">F</label>
                            </label>
                        </>
                        <>
                            <label
                                onClick={() => {
                                    setIsOther(true);
                                    setIsSexAtBirthMale(false);
                                    setIsSexAtBirthFemale(false);
                                    setSelectedSexLookupId(otherLookupId);
                                }}
                                className="female-label no-bottom-margin"
                            >
                                <input checked={isOther} type="radio" />
                                <label className="value-label">{getText("OtherLabel")}</label>
                            </label>
                        </>
                    </div>
                </div>
            </>
        );
    }

    function renderFooter(): JSX.Element {
        return (
            <div className="p-footer">
                <div className="row">
                    <div className="col-lg-12 col-md-12 col-sm-12 columns text-right">
                        <button
                            className="btn btn-danger push-right-5"
                            onClick={() => props.callback({ SexAtBirthLookupId: null })}
                        >
                            {getText("IgnoreLabel")}
                        </button>
                        <button
                            className="btn btn-primary"
                            onClick={() => save()}
                            disabled={isSaveDisabled()}
                        >
                            {getText("SaveLabel")}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    function isSaveDisabled(): boolean {
        return !selectedSexLookupId;
    }

    async function save(): Promise<void> {
        const response = await Client.postAsync<any>(
            ServerOperations.Server.Myle_Patient_SetRequiredInformation,
            {
                PatientContactId: props.patientContactId,
                SexAtBirthLookupId: selectedSexLookupId
            }
        );

        if (response?.errors?.any())
            return;

        props.callback({
            SexAtBirthLookupId: selectedSexLookupId
        });
    }

    function determineWarningToRender(): JSX.Element[] {
        let renderOption: JSX.Element[] = [];

        props.warnings.forEach(p => {
            switch (p) {
                case PossiblePatientWarning.SexAtBirthMissing:
                    renderOption.push(renderSexAtBirthMandatorySection());
                    break;

                default:
                    throw new Error("This missing information check is not implemented");
                    break;
            }
        });

        return renderOption;
    }

    return (
        <Modal
            title={getText("CompleteSexAtBirthLabel")}
            id="PatientMissingInfoWarningPopup"
            footer={renderFooter()}
            draggable
            showOverlay
            width={550}
        >
            {determineWarningToRender()}
        </Modal>
    );
}

export { PatientMissingInfoWarning };
